/**
 * Parent module (v1) - content aligned with Parent_Module_LMS_Content_Mapping.md
 * Pause - Reflect - Act structure across Sara / Anika / Aman; no standalone barriers section.
 */

import { EDUCATOR_M2_SARA_STORY_YOUTUBE_VIDEO_ID } from './educatorModuleContent';
import { COMMUNITY_M2_AMAN_STORY_YOUTUBE_VIDEO_ID } from './communityModuleContent';

function trimReactEnv(name) {
  const v = process.env[name];
  if (v == null) return '';
  return String(v).trim();
}

export const PARENT_CONTENT = {
  id: 'parent-module',
  title: 'Parent Module',
  subtitle: 'Before the Gate: A Parent Lens on the STEMM Pathway',
  description:
    'For parents and guardians who want to understand how South Asian women\'s STEMM journeys unfold across time.',
  courseDescriptionLong:
    'This module is designed for parents and guardians who want to understand how South Asian women\'s STEMM journeys unfold across time. It draws on lived experiences from national research with South Asian women across education, early career, and mid-career stages, and translates those patterns upstream for family reflection.',
  learningGoals: [
    'Recognize how education, workplace, and community barriers show up across the STEMM pathway.',
    'Reflect on how family norms can shape access to opportunity.',
    'Choose one small shift they can make at home.',
  ],
  sections: [
    {
      id: 'overview',
      title: 'Module Purpose & Learning Goals',
      content:
        '<h3>Before the Gate</h3><p>This module is designed for <strong>parents and guardians</strong> who want to understand how South Asian women\'s STEMM journeys unfold across time.</p><p>Meta-reflexivity is introduced as a lens you will practice across three story-based sections.</p>',
    },
    {
      id: 'sara-story',
      title: "Sara's Story",
      content:
        '<h3>Case Study: Sara</h3><p>Sara\'s story shows what happens when a student has the right credential but is not able to reliably convert that into opportunity. Notice the conditions around her - not whether she made the right choices.</p>',
    },
    {
      id: 'anika-story',
      title: "Anika's Story",
      content:
        '<h3>Case Study: Anika</h3><p>Anika\'s story shows what can happen after a South Asian woman enters the workplace with strong qualifications - when hard work alone does not lead to recognition or advancement.</p>',
    },
    {
      id: 'aman-story',
      title: "Aman's Story",
      content:
        '<h3>Case Study: Aman</h3><p>Aman\'s story shows how family and community expectations can shape a daughter\'s sense of autonomy and ambition. Notice how ordinary comments and concerns accumulate over time.</p>',
    },
    {
      id: 'meta-reflexivity',
      title: 'Meta-Reflexivity',
      content: '',
    },
    {
      id: 'parent-survey',
      title: 'Parent Module Survey',
      content: '',
    },
  ],
};

/** Part 1 slide 1 - module overview video (same pattern as educator / community Part 1). */
export const PARENT_M1_OVERVIEW_YOUTUBE_VIDEO_ID = 'AR3R8U7s40o';

export const PARENT_M1_SLIDES = [
  {
    title: 'How to Use this Module',
    youtubeVideoId: PARENT_M1_OVERVIEW_YOUTUBE_VIDEO_ID,
  },
  {
    title: 'Module Purpose & Learning Goals',
    goalLines: [
      'Recognize how education, workplace, and community barriers show up across the STEMM pathway.',
      'Reflect on how family norms can shape access to opportunity.',
      'Choose one small shift they can make at home.',
    ],
    whyParentsHeading: 'Why Start with Parents?',
    bodyLines: [
      'Families are often the first place where daughters learn what is possible, what is expected, and what must be negotiated. Long before a hiring manager evaluates a resume or a supervisor decides who receives an opportunity, young women are already developing beliefs about leadership, risk, and self-worth.',
      'As you move through the module, you will be invited to practice meta-reflexivity: looking beyond individual choices to examine the beliefs, cultural narratives, and systems that shape how we interpret success. This module does not suggest that families are responsible for the systemic barriers South Asian women encounter in STEMM. Rather, it translates those patterns upstream and invites reflection on the ways family environments can either strengthen or constrain a daughter\'s ability to navigate those barriers when they arise.',
      'Please note, our research participants were 18+, hence, this module intentionally does not cover material regarding younger girls in STEMM.',
    ],
    closingLine: "Let's start with the first story of Sara.",
  },
];

/**
 * Part 3 - Anika case study video: same sources as workplace module
 * (local MP4 / REACT_APP_ANIKA_VIDEO_URL). Optional YouTube override if set.
 */
export const PARENT_ANIKA_STORY_YOUTUBE_VIDEO_ID = trimReactEnv('REACT_APP_PARENT_ANIKA_STORY_YOUTUBE_VIDEO_ID');

export const PARENT_ANIKA_CASE_STUDY_VIDEO = {
  localSrc: 'videos/anika-case-study.mp4',
  remoteSrc: trimReactEnv('REACT_APP_ANIKA_VIDEO_URL'),
  poster: trimReactEnv('REACT_APP_ANIKA_VIDEO_POSTER') || 'images/thumb1.jpg',
  title: "Anika's Story",
};

/** Part 4 - Aman case study video: same YouTube as community Part 2. */
export const PARENT_AMAN_STORY_YOUTUBE_VIDEO_ID = COMMUNITY_M2_AMAN_STORY_YOUTUBE_VIDEO_ID;

/** Story section config keyed by syllabus section id. */
export const PARENT_STORY_SECTIONS = {
  'sara-story': {
    storyKey: 'sara',
    character: 'Sara',
    videoTitle: 'Almost there.',
    youtubeVideoId: EDUCATOR_M2_SARA_STORY_YOUTUBE_VIDEO_ID,
    introSlide: {
      title: 'Why This Story Matters',
      lines: [
        "Let's start with the first story of Sara.",
        'Sara\'s story shows what happens when a student has the right credential, but is not able to reliably convert that into opportunity. As you watch the video, resist the urge to diagnose or prescribe advice to Sara herself, instead notice the conditions around her that contribute to the barriers.',
      ],
      closingLine: "Let's begin.",
    },
    pausePrompt: 'What are your initial reactions to Sara\'s story?',
    pauseLines: [],
    researchInsight: {
      showAfterForm: true,
      bodyLines: [
        'Similar to the educator module, we have developed a resource for workplace leaders to more deeply reflect and act on the barriers our research has uncovered. This includes barriers such as "cultural fit": assumptions about who "seems" like a leader, who belongs in certain roles, or who is the right fit.',
        {
          variant: 'emphasis',
          html:
            'Our Breaking Barriers research shows that 65.9% of South Asian women in STEMM report a distinct lack of mentorship during their education, while 62.7% experience critical gaps in leadership representation.',
          statsTable: {
            headers: ['Findings*', 'Mentorship Matrix*', 'Data Context*'],
            rows: [
              ['65.9%', 'Lack of Guidance', 'Lacked mentorship and advocacy'],
              ['42%', 'Peer Support', 'Peer mentoring proved superior'],
              ['62.7%', 'Representative Gap', 'Gaps in mentorship overlapped'],
            ],
            footnote: '*Assume We Can survey data: n=194, Lotus STEMM (2025)',
          },
        },
        'Once again, it confirms what South Asian communities have long known: getting a STEMM job is important but it is not the end of the pathway. Advancement often depends on recognition, sponsorship, fair evaluation, flexibility, and support during caregiving or life transitions.',
      ],
      linkModuleId: 'workplace-module',
      linkLabel: 'Explore the Workplace Module',
    },
    reflectTitle: 'Reflect',
    reflectIntro: 'Parents are invited to reflect on three guiding questions:',
    reflectQuestions: [
      'Do we ask our children about mentors as often as marks?',
      'If an opportunity required travel, residence, or relocation, would our support be predictable?',
      'Are we helping our daughter build confidence to ask for guidance, references, and introductions?',
    ],
    actTitle: 'Act',
    actPrompt:
      'A small step to taking action is to reflect on what questions you will ask differently - especially when it comes to treating mentorship and sponsorship as a family-supported practice. Here are some we heard - feel free to share yours in the comments.',
    actExamples: [
      'Who is helping her navigate the unwritten rules of the next step?',
      'What would predictable support look like if she needed to relocate or travel?',
      'When did we last ask about mentors - not just marks or deadlines?',
    ],
    actHideExamplesHeading: true,
    actPlaceholder: 'Share one question you might ask differently…',
    transitionLine:
      'Now, let\'s move to Anika\'s story - an employment scenario - where the barriers become less about admission and more about recognition, advancement, and belonging.',
  },
  'anika-story': {
    storyKey: 'anika',
    character: 'Anika',
    videoTitle: 'Put your head down.',
    youtubeVideoId: PARENT_ANIKA_STORY_YOUTUBE_VIDEO_ID,
    caseVideo: PARENT_ANIKA_CASE_STUDY_VIDEO,
    introSlide: {
      title: 'Why This Story Matters',
      lines: [
        'Anika\'s story shows what can happen after a South Asian woman enters the workplace with strong qualifications and commitment, but finds that hard work alone does not lead to recognition or advancement.',
        'As you watch the video, notice how the barriers are not always loud or obvious. Pay attention to the small comments, assumptions, and decisions that shape how Anika is seen.',
      ],
      closingLine: "Let's begin.",
    },
    pausePrompt: 'What are your initial reactions to Anika\'s story?',
    pauseLines: [],
    researchInsight: {
      showAfterForm: true,
      bodyLines: [
        'Similar to the educator module, we have developed a resource for workplace leaders to more deeply reflect and act on the barriers our research has uncovered. This includes barriers such as "cultural fit": assumptions about who "seems" like a leader, who belongs in certain roles, or who is the right fit.',
        {
          variant: 'researchImage',
          src: '/images/anikastatnew.jpg',
          alt: 'Factors affecting career progression by education level (Breaking Barriers, 2025)',
          caption: 'Fig.: Factors affecting Career Progression (Generated via NVivo)*',
        },
        'Once again, it confirms what South Asian communities have long known: getting a STEMM job is important but it is not the end of the pathway. Advancement often depends on recognition, sponsorship, fair evaluation, flexibility, and support during caregiving or life transitions.',
      ],
      linkModuleId: 'workplace-module',
      linkLabel: 'Explore the Workplace Module',
    },
    reflectTitle: 'Reflect',
    reflectIntro: 'Parents are invited to reflect on three guiding questions:',
    reflectQuestions: [
      'What did our own journeys teach us about success, safety, and advancement?',
      'Have our values shifted over time, and are we making room for her pathway to look different from ours?',
      'If she faces bias or exclusion at work, do we help her name it as a structural barrier rather than a personal failure?',
    ],
    actTitle: 'Act',
    actPrompt:
      'What questions can you ask to help your daughter - regardless of age - maintain advocacy?',
    actExamples: [
      'Who is advocating for her at work - and who could?',
      'When she describes being overlooked, do we listen for patterns rather than offering quick fixes?',
      'What would it look like to support her in naming bias without blaming herself?',
    ],
    actPlaceholder: 'Share one question you might ask to support advocacy…',
    transitionLine: 'Now, let\'s move to a community scenario.',
  },
  'aman-story': {
    storyKey: 'aman',
    character: 'Aman',
    videoTitle: 'For your own benefit.',
    youtubeVideoId: PARENT_AMAN_STORY_YOUTUBE_VIDEO_ID,
    introSlide: {
      title: 'Why This Story Matters',
      lines: [
        'Aman\'s story shows how family and community expectations can shape a daughter\'s sense of autonomy and ambition. As you watch the video, resist the urge to focus only on whether any one person means harm. Instead, notice how ordinary comments, concerns, or comparisons can accumulate over time.',
      ],
      closingLine: "Let's begin.",
    },
    pausePrompt: 'What are your initial reactions to Aman\'s story?',
    pauseLines: [],
    researchInsight: {
      showAfterForm: true,
      bodyLines: [
        'Aman\'s story raises the question: do we recognize how family and community expectations shape what daughters feel allowed to pursue?',
        {
          variant: 'researchImage',
          src: '/images/amanstat1.jpg',
          alt: 'Communal pressures, honor culture, and marriage pressure — Canada-born (second gen.) vs Immigrants (first gen.)',
        },
        'Our research quantifies this tension, revealing 55% of second-generation immigrants face intense communal pressures, while 52% of the first-generation immigrants experience acute marriage pressures when pursuing independence, mobility, or caregiving negotiations. For this reason, we have developed a separate learning module for community leaders.',
      ],
      linkModuleId: 'community-module',
      linkLabel: 'Explore the Community Module',
    },
    reflectTitle: 'Reflect',
    reflectIntro:
      'Aman\'s story invites reflection on how values such as care, safety, respect, sacrifice, and family responsibility are practiced at home. Parents are invited to reflect on:',
    reflectQuestions: [
      'Do sons and daughters carry different expectations around freedom, care, reputation, or responsibility?',
      'Have our own values around marriage, career, caregiving, and independence shifted over time?',
      'Are we making room for our daughters\' pathways to reflect the world they are navigating now?',
    ],
    actTitle: 'Act',
    actPrompt: 'What is one question you could ask differently after watching Aman\'s story?',
    actIntro:
      'A small shift can begin by making family support more predictable. Instead of waiting until a major decision arrives, families can talk earlier about expectations and assumptions around values such as autonomy, mobility, or caregiving.',
    actExamples: [
      'What would predictable support look like if she wanted to stretch, relocate, or take on visibility?',
      'When concern shows up as questions about timing or reputation, what are we really protecting?',
      'How might we talk about autonomy and care in the same conversation?',
    ],
    actPlaceholder: 'Share one question you could ask differently…',
    transitionLine: null,
  },
};

export const PARENT_STORY_SECTION_IDS = ['sara-story', 'anika-story', 'aman-story'];

/** Part 5 - Meta-Reflexivity slides. */
export const PARENT_M5_HEADER = 'Meta-Reflexivity: Moving from Awareness to Action';

export const PARENT_M5_OVERVIEW_YOUTUBE_VIDEO_ID = 'SS9kf1IWCyo';
export const PARENT_M5_CLOSING_YOUTUBE_VIDEO_ID = 'hV55DeGcuGA';

export const PARENT_M5_SLIDES = [
  {
    title: 'Closing & Additional Resources',
    youtubeVideoId: PARENT_M5_CLOSING_YOUTUBE_VIDEO_ID,
  },
  {
    title: 'Meta-Reflexivity as Practice',
    lines: [
      'Developed by sociologist Margaret Archer (2007), meta-reflexivity is a way of noticing how our values, fears, hopes, and assumptions shape everyday decisions.',
      'Throughout the module, each story invited parents to pause and notice their first reaction, reflect on the barriers and assumptions shaping the situation, and act by identifying one small shift in the questions they ask or the support they offer.',
      'The Pause - Reflect - Act structure used across all three story sections is the meta-reflexivity practice in action. You have already started doing this work.',
    ],
  },
  {
    title: PARENT_M5_HEADER,
    youtubeVideoId: PARENT_M5_OVERVIEW_YOUTUBE_VIDEO_ID,
  },
];

/** Part 6 - Survey copy (structure mirrors workplace survey in StudyModule.js). */
export const PARENT_SURVEY_INTRO = {
  title: 'Parent Module - Post-Module Experience',
  body: 'As a participant in the Parent Module, we are interested in understanding what you gained from participating and how your understanding may have shifted compared to before the module.',
  pausePrompt:
    'Please pause before you continue. Many insights only become visible in hindsight. Take a moment to reflect on your participation before you answer.',
};

export const PARENT_SURVEY_SUBMITTED_TITLE = 'Thank you for participating in the Parent Module!';

export const PARENT_META_PRIVATE_NOTE_KEY = 'lms-parent-meta-private-note';

export const PARENT_PAUSE_STORAGE_PREFIX = 'lms-parent-pause';
export const PARENT_ACT_STORAGE_PREFIX = 'lms-parent-act';
export const PARENT_SURVEY_STORAGE_KEY = 'lms-parent-module-survey-submissions';
export const PARENT_PARTICIPANT_REVIEW_STORAGE_KEY = 'lms-parent-participant-review';
